import type Canvas from "diagram-js/lib/core/Canvas";
import type EventBus from "diagram-js/lib/core/EventBus";
import type EventStormingModeling from "../modeling/EventStormingModeling.js";
import { STICKY_KINDS, type EventStormingShape } from "../model/di-types.js";
import { STICKY_STYLES } from "../draw/styles.js";
import { isKeyOnHiddenTarget } from "../keyboard/index.js";

/** The 8 swatches = the 8 sticky fills (recolor any element into another kind's tone). */
const SWATCHES: ReadonlyArray<{ value: string; title: string }> = STICKY_KINDS.map((kind) => ({
  value: STICKY_STYLES[kind].fill,
  title: STICKY_STYLES[kind].label,
}));

/**
 * Compact color picker (bpmn.io-style): a 3x3 grid of small color squares (no text), opened from
 * an element's context pad. Uses its own popover (instead of diagram-js PopupMenu) so the swatch
 * grid can be rendered exactly and without text. Sets the color override via
 * `eventStormingModeling` (undo/redo). First cell = "no color" (back to the kind's default).
 */
export default class EventStormingColorPicker {
  static $inject = ["canvas", "eventBus", "eventStormingModeling"];

  private popover: HTMLElement | undefined;

  private readonly onDocPointer = (e: MouseEvent): void => {
    if (this.popover && !this.popover.contains(e.target as Node)) this.close();
  };
  private readonly onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape" && !isKeyOnHiddenTarget(e)) this.close();
  };

  constructor(
    private readonly canvas: Canvas,
    eventBus: EventBus,
    private readonly modeling: EventStormingModeling,
  ) {
    // Close on pan/zoom, drag or re-import (otherwise the popover ends up misplaced).
    eventBus.on(["canvas.viewbox.changing", "drag.init", "diagram.clear"], () => this.close());
  }

  /** Opens the swatch grid for `shape` at the click position (viewport coordinates). */
  open(shape: EventStormingShape, clientX: number, clientY: number): void {
    this.close();
    const container = this.canvas.getContainer();
    const rect = container.getBoundingClientRect();

    const pop = document.createElement("div");
    pop.className = "event-storming-color-picker";

    const swatches: ReadonlyArray<{ value: string | undefined; title: string; cls: string }> = [
      { value: undefined, title: "No color", cls: "event-storming-cp-none" },
      ...SWATCHES.map((c) => ({ value: c.value as string, title: c.title, cls: "" })),
    ];
    for (const s of swatches) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `event-storming-cp-swatch ${s.cls}`.trim();
      btn.title = s.title;
      if (s.value) btn.style.background = s.value;
      if ((shape.color ?? undefined) === s.value) btn.classList.add("event-storming-cp-active");
      btn.addEventListener("click", (ev) => {
        if (isKeyOnHiddenTarget(ev)) return;
        ev.stopPropagation();
        this.modeling.setColor(shape, s.value);
        this.close();
      });
      pop.append(btn);
    }

    container.appendChild(pop);

    // Keep it within the (overflow:hidden) container.
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    const pw = pop.offsetWidth;
    const ph = pop.offsetHeight;
    const left = Math.max(4, Math.min(clientX - rect.left, cw - pw - 4));
    const top = Math.max(4, Math.min(clientY - rect.top, ch - ph - 4));
    pop.style.left = `${left}px`;
    pop.style.top = `${top}px`;

    this.popover = pop;
    // Attach listeners with a delay so the opening click does not immediately close the popover.
    setTimeout(() => {
      document.addEventListener("click", this.onDocPointer, true);
      document.addEventListener("keydown", this.onKey, true);
    }, 0);
  }

  close(): void {
    if (!this.popover) return;
    this.popover.remove();
    this.popover = undefined;
    document.removeEventListener("click", this.onDocPointer, true);
    document.removeEventListener("keydown", this.onKey, true);
    // A removed swatch leaves the focus on <body>, where the board's keys do not act. Like
    // diagram-js's popup menu, hand it back unless the closing click put it somewhere else.
    this.canvas.restoreFocus();
  }
}
